import { z } from 'zod';

const localDateTimeSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, {
    message: 'Expected a local date and time in YYYY-MM-DDTHH:mm format.',
  });

const optionalText = z.string().trim().max(4000).optional().default('');

export const ingestTestModeSchema = z.enum([
  'new_case',
  'update_result',
  'replay_dft',
]);

export const ingestTestTariffSchema = z
  .object({
    tariffID: z.guid().optional(),
    fullCode: z
      .string()
      .trim()
      .regex(/^\d{2,6}$/, 'Tariff code must contain 2 to 6 digits.'),
    units: z.number().int().min(1).max(999).default(1),
    icd9: z.string().trim().max(6).optional().default(''),
    incision: z.enum(['', 'B', 'D', 'S', 'L', 'R']).optional().default(''),
    split: z
      .enum([
        '',
        'A',
        'C',
        'D',
        'E',
        'G',
        'H',
        'K',
        'L',
        'N',
        'P',
        'S',
        'T',
        'U',
        'V',
        'W',
        'X',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
      ])
      .optional()
      .default(''),
  })
  .strict();

export const ingestTestRequestSchema = z
  .object({
    mode: ingestTestModeSchema.default('new_case'),
    testCaseID: z
      .string()
      .trim()
      .min(6)
      .max(64)
      .regex(
        /^QA-[A-Za-z0-9_-]+$/,
        'Test case ID must begin with QA- and contain only letters, numbers, underscores, and hyphens.'
      )
      .optional(),
    siteID: z.guid(),
    providerID: z.guid(),
    payorID: z.guid(),
    patient: z
      .object({
        firstName: z.string().trim().min(1).max(100),
        lastName: z.string().trim().min(1).max(100),
        dateOfBirth: z.string().date(),
        sex: z.enum(['M', 'F', 'X']),
        address: z.string().trim().min(1).max(255),
        city: z.string().trim().min(1).max(100),
        province: z.string().trim().min(2).max(2),
        postalCode: z
          .string()
          .trim()
          .regex(/^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/),
        country: z.string().trim().min(1).max(100).default('Canada'),
        phone: optionalText,
        email: z.union([z.email(), z.literal('')]).default(''),
        maritalStatus: optionalText,
        healthcardNumber: z.string().trim().max(32).default(''),
        healthcardRegistration: z.string().trim().max(32).default(''),
        healthcardProvince: z.string().trim().min(2).max(2),
      })
      .strict(),
    claim: z
      .object({
        serviceStart: localDateTimeSchema,
        serviceEnd: localDateTimeSchema,
        diagnosis: z.string().trim().max(6).default(''),
        locationOfService: z.string().trim().max(32).default(''),
        virtualPhysicianRegion: z.string().trim().max(32).default(''),
        referringProvider: z.string().trim().max(32).default('00000'),
        reportText: z.string().trim().min(1).max(4000),
        providerNotes: optionalText,
        resultStatus: z.enum(['P', 'F', 'A']).default('F'),
        procedureCode: z.string().trim().min(1).max(64).default('QA-TEST'),
        procedureDescription: z
          .string()
          .trim()
          .min(1)
          .max(255)
          .default('Synthetic QA ingest test'),
        modality: z.string().trim().min(1).max(16).default('QA'),
        facilityName: optionalText,
        facilityRoom: optionalText,
        studyType: z.string().trim().max(32).default('QA'),
        serviceLocationIndicator: optionalText,
        optometristReason: optionalText,
        patientCount: z.number().int().min(1).max(999).default(1),
        confidentialCode: optionalText,
        thirdPartyLiability: optionalText,
        incorporated: z.string().trim().max(16).default('0'),
      })
      .strict(),
    tariffs: z.array(ingestTestTariffSchema).min(1).max(50),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.mode !== 'new_case' && !value.testCaseID) {
      context.addIssue({
        code: 'custom',
        path: ['testCaseID'],
        message: 'An existing test case ID is required for this mode.',
      });
    }
  });

export const ingestTestClaimSummarySchema = z
  .object({
    id: z.guid(),
    status: z.string(),
    tariffCount: z.number().int().nonnegative(),
  })
  .strict();

export const ingestTestResponseSchema = z
  .object({
    success: z.boolean(),
    testCaseID: z.string(),
    messageID: z.string(),
    outcome: z.string(),
    stages: z.array(
      z
        .object({
          messageType: z.enum(['ORU', 'DFT']),
          success: z.boolean(),
          error: z.string().optional(),
        })
        .strict()
    ),
    claims: z.array(ingestTestClaimSummarySchema),
  })
  .strict();

export type IngestTestRequest = z.infer<typeof ingestTestRequestSchema>;
export type IngestTestResponse = z.infer<typeof ingestTestResponseSchema>;
